import type { FC } from 'react'

import { StyledText } from '@skytech/ui'

import type { Policy } from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'

import type { GrossSalarySchemeProductPriceProps } from './GrossSalarySchemeProductPrice'
import {
  GrossSalarySchemeProductPrice,
  GrossSalarySchemeProductPriceWizard,
} from './GrossSalarySchemeProductPrice'
import type { ProductPriceProps } from './ProductPrice'
import { ProductPrice } from './ProductPrice'

type DisplayMode = 'default' | 'grossSalaryScheme'

type ProductPriceDisplayProps = {
  isSelectionDisabled: boolean
  displayMode: DisplayMode
  grossSalaryTaxBracket?: 'Top' | 'Base' | null
} & ProductPriceProps &
  Omit<GrossSalarySchemeProductPriceProps, 'isTopTaxBracket' | 'price'>

export const ProductPriceDisplay: FC<ProductPriceDisplayProps> = ({
  isSelectionDisabled,
  displayMode,
  isFromPriceTextVisible,
  isSingleProductGroup,
  grossSalaryTaxBracket,
  employeePrice,
  productPrice,
  isProductPriceVisible,
}) => (
  <div className="w-full">
    {isSelectionDisabled ? (
      <>
        {!isSingleProductGroup && isFromPriceTextVisible && (
          <StyledText
            size="small"
            colorMode="gray"
            className="!leading-none max-sm:!text-xs sm:!mb-0.5"
          >
            <Translation id="from" />{' '}
          </StyledText>
        )}
        <StyledText
          size="xl"
          colorMode="black"
          fontWeight="bold"
          className="max-mobile:!text-lg flex flex-col !leading-none max-md:!text-xl"
        >
          {employeePrice === 0 ? (
            <Translation id="Fully covered" />
          ) : (
            <FormattedNumberWithCurrency isTextBold amount={employeePrice} />
          )}
        </StyledText>
      </>
    ) : (
      <>
        {/* Salary deduction case */}
        {displayMode === 'default' && (
          <ProductPrice
            isFromPriceTextVisible={isFromPriceTextVisible}
            isSingleProductGroup={isSingleProductGroup}
            employeePrice={employeePrice}
            productPrice={productPrice}
            isProductPriceVisible={isProductPriceVisible}
          />
        )}
        {displayMode === 'grossSalaryScheme' && (
          <GrossSalarySchemeProductPrice
            price={employeePrice}
            isSingleProductGroup={isSingleProductGroup}
            isFromPriceTextVisible={isFromPriceTextVisible}
            isTopTaxBracket={grossSalaryTaxBracket === 'Top'}
          />
        )}
      </>
    )}
  </div>
)

export const ProductPriceDisplayWizard: FC<
  ProductPriceDisplayProps & {
    data: Pick<Policy, 'coverInterval' | 'grossSalaryTopRate' | 'grossSalaryBaseRate'>
  }
> = ({
  data,
  isSelectionDisabled,
  displayMode,
  isFromPriceTextVisible,
  isSingleProductGroup,
  grossSalaryTaxBracket,
  employeePrice,
  productPrice,
  isProductPriceVisible,
}) => (
  <div className="w-full">
    {isSelectionDisabled ? (
      <>
        {!isSingleProductGroup && isFromPriceTextVisible && (
          <StyledText
            size="small"
            colorMode="gray"
            className="!leading-none max-sm:!text-xs sm:!mb-0.5"
          >
            <Translation id="from" />{' '}
          </StyledText>
        )}
        <StyledText
          size="xl"
          colorMode="black"
          fontWeight="bold"
          className="max-mobile:!text-lg flex flex-col !leading-none max-md:!text-xl"
        >
          {employeePrice === 0 ? (
            <Translation id="Fully covered" />
          ) : (
            <FormattedNumberWithCurrency isTextBold amount={employeePrice} />
          )}
        </StyledText>
      </>
    ) : (
      <>
        {displayMode === 'default' && (
          <ProductPrice
            isFromPriceTextVisible={isFromPriceTextVisible}
            isSingleProductGroup={isSingleProductGroup}
            employeePrice={employeePrice}
            productPrice={productPrice}
            isProductPriceVisible={isProductPriceVisible}
          />
        )}
        {displayMode === 'grossSalaryScheme' && (
          <GrossSalarySchemeProductPriceWizard
            data={data}
            price={employeePrice}
            isSingleProductGroup={isSingleProductGroup}
            isFromPriceTextVisible={isFromPriceTextVisible}
            isTopTaxBracket={grossSalaryTaxBracket === 'Top'}
          />
        )}
      </>
    )}
  </div>
)
